// src/pages/finance/BankAccountDetail.jsx
// Single bank account view — scoped summary cards, deposit/withdraw,
// editable account info, and an editable transaction history.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Landmark, Wallet, ArrowDownCircle, ArrowUpCircle, ArrowDownLeft,
  Loader2, X, Plus, Minus, AlertCircle, CheckCircle2, Pencil, ArrowLeft, Trash2,
} from 'lucide-react';
import { bankAccountService } from '../../services/bankAccountService';

const ACCOUNT_TYPES = ['Current', 'Savings'];

const fmt = (n) => 'PKR ' + Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Scoped Summary Card (same gradient style as the list page) ──────────
const SummaryCard = ({ icon: Icon, title, mainValue, subLabel, subValue, gradient, glow, mainValueClassName = '' }) => (
  <div className={'relative overflow-hidden rounded-card p-6 text-white shadow-elevated transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl ' + gradient}>
    <div className="bank-card-sweep" />
    <div className="relative z-10">
      <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-widest text-white/70">{title}</p>
      <p className={'text-2xl font-bold mt-1 tracking-tight truncate ' + mainValueClassName}>{mainValue}</p>
      <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between">
        <span className="text-xs text-white/70">{subLabel}</span>
        <span className="text-sm font-semibold">{subValue}</span>
      </div>
    </div>
    <div className={'pointer-events-none absolute -bottom-8 -right-8 w-32 h-32 rounded-full blur-2xl opacity-40 ' + glow} />
  </div>
);

// ── Deposit / Withdraw Modal ──────────────────────────────────────────────
const TxnModal = ({ type, onClose, onDone, accountId, currentBalance }) => {
  const [form, setForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0], purpose: '', person_name: '', reference_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isDeposit = type === 'deposit';
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.amount || Number(form.amount) <= 0) return setError('Enter a valid amount.');
    if (isDeposit) {
      if (!form.person_name.trim()) return setError('Person name is required.');
    } else {
      if (!form.purpose.trim()) return setError('Purpose is required.');
    }
    // Feature: withdrawals are allowed to push the account negative — no client-side block.

    setSubmitting(true);
    try {
      const call = isDeposit ? bankAccountService.deposit : bankAccountService.withdraw;
      await call(accountId, { ...form, amount: Number(form.amount) });
      onDone();
    } catch (err) {
      setError(err.message || 'Transaction failed');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary';
  const labelCls = 'block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md overflow-hidden">
        <div className={'px-6 py-4 flex items-center justify-between font-bold text-white ' + (isDeposit ? 'bg-gradient-to-r from-emerald-500 to-green-600' : 'bg-gradient-to-r from-rose-500 to-red-600')}>
          {isDeposit ? 'Deposit Amount' : 'Withdraw Amount'}
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label className={labelCls}>Amount</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={form.amount} onChange={(e) => setField('amount', e.target.value)} placeholder="0.00" />
          </div>
          {isDeposit ? (
            <div>
              <label className={labelCls}>Deposited By</label>
              <input className={inputCls} value={form.person_name} onChange={(e) => setField('person_name', e.target.value)} placeholder="Person name" />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Purpose</label>
                <input className={inputCls} value={form.purpose} onChange={(e) => setField('purpose', e.target.value)} placeholder="e.g. Office rent, Supplier payment" />
              </div>
              <div>
                <label className={labelCls}>Person Name (optional)</label>
                <input className={inputCls} value={form.person_name} onChange={(e) => setField('person_name', e.target.value)} placeholder="Withdrawn by" />
              </div>
            </>
          )}
          <div>
            <label className={labelCls}>Transaction ID (optional)</label>
            <input className={inputCls} value={form.reference_number} onChange={(e) => setField('reference_number', e.target.value)} placeholder="Bank transaction / reference no." />
          </div>
          <div>
            <label className={labelCls}>Date</label>
            <input type="date" className={inputCls} value={form.date} onChange={(e) => setField('date', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Description (optional)</label>
            <textarea className={inputCls + ' h-20'} value={form.description} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className={
              'w-full text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg transition-all ' +
              (isDeposit ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20' : 'bg-red-600 hover:bg-red-700 shadow-red-500/20')
            }
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm {isDeposit ? 'Deposit' : 'Withdraw'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Edit Transaction Modal ────────────────────────────────────────────────
const EditTxnModal = ({ txn, onClose, onDone, accountId }) => {
  const isDeposit = txn.type === 'deposit';
  const [form, setForm] = useState({
    amount: txn.amount, description: txn.description || '', date: txn.date, purpose: txn.purpose || '', person_name: txn.person_name || '', reference_number: txn.reference_number || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.amount || Number(form.amount) <= 0) return setError('Enter a valid amount.');
    if (isDeposit) {
      if (!form.person_name.trim()) return setError('Person name is required.');
    } else {
      if (!form.purpose.trim()) return setError('Purpose is required.');
    }

    setSubmitting(true);
    try {
      await bankAccountService.updateTransaction(accountId, txn.transaction_id, { ...form, amount: Number(form.amount) });
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to update transaction');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary';
  const labelCls = 'block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
          Edit {txn.type === 'deposit' ? 'Deposit' : 'Withdraw'} — {txn.transaction_id}
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label className={labelCls}>Amount</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={form.amount} onChange={(e) => setField('amount', e.target.value)} />
          </div>
          {isDeposit ? (
            <div>
              <label className={labelCls}>Person Name</label>
              <input className={inputCls} value={form.person_name} onChange={(e) => setField('person_name', e.target.value)} />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Purpose</label>
                <input className={inputCls} value={form.purpose} onChange={(e) => setField('purpose', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Person Name (optional)</label>
                <input className={inputCls} value={form.person_name} onChange={(e) => setField('person_name', e.target.value)} />
              </div>
            </>
          )}
          <div>
            <label className={labelCls}>Transaction ID</label>
            <input className={inputCls} value={form.reference_number} onChange={(e) => setField('reference_number', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Date</label>
            <input type="date" className={inputCls} value={form.date} onChange={(e) => setField('date', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea className={inputCls + ' h-20'} value={form.description} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Edit Account Modal ────────────────────────────────────────────────────
const EditAccountModal = ({ account, onClose, onDone }) => {
  const [form, setForm] = useState({
    bank_name: account.bank_name, account_owner: account.account_owner, account_number: account.account_number,
    iban: account.iban, swift_code: account.swift_code || '', branch_location: account.branch_location,
    account_type: account.account_type, status: account.status, notes: account.notes || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.bank_name.trim()) return setError('Bank name is required.');
    if (!form.account_owner.trim()) return setError('Account owner is required.');

    setSubmitting(true);
    try {
      await bankAccountService.updateAccount(account.account_id, form);
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to update account');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary';
  const labelCls = 'block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900 sticky top-0 bg-white">
          Edit Bank Account
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label className={labelCls}>Bank Name</label>
            <input className={inputCls} value={form.bank_name} onChange={(e) => setField('bank_name', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Account Owner</label>
            <input className={inputCls} value={form.account_owner} onChange={(e) => setField('account_owner', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Account Number</label>
              <input className={inputCls} value={form.account_number} onChange={(e) => setField('account_number', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>IBAN</label>
              <input className={inputCls} value={form.iban} onChange={(e) => setField('iban', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SWIFT Code</label>
              <input className={inputCls} value={form.swift_code} onChange={(e) => setField('swift_code', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Account Type</label>
              <select className={inputCls} value={form.account_type} onChange={(e) => setField('account_type', e.target.value)}>
                {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Branch Location</label>
            <input className={inputCls} value={form.branch_location} onChange={(e) => setField('branch_location', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select className={inputCls} value={form.status} onChange={(e) => setField('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea className={inputCls + ' h-20'} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Delete Confirmation Modal ───────────────────────────────────────────
const DeleteTxnModal = ({ txn, onClose, onDone, accountId }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setSubmitting(true);
    setError('');
    try {
      await bankAccountService.deleteTransaction(accountId, txn.transaction_id);
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to delete transaction');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-card w-full max-w-sm p-6 shadow-elevated">
        <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-4">
          <Trash2 className="w-5 h-5" />
        </div>
        <h3 className="text-base font-bold text-gray-900">Delete Transaction</h3>
        <p className="text-sm text-gray-500 mt-1">
          This will permanently delete {txn.type === 'deposit' ? 'deposit' : 'withdrawal'} {txn.transaction_id}. This action cannot be undone.
        </p>
        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-xs font-semibold bg-red-50 p-2.5 rounded-lg">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={submitting}
            className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-bold text-sm flex justify-center items-center gap-2 hover:bg-red-700 transition-all"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────
const BankAccountDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnModal, setTxnModal] = useState(null); // 'deposit' | 'withdraw' | null
  const [editTxn, setEditTxn] = useState(null);
  const [deleteTxn, setDeleteTxn] = useState(null);
  const [editAccount, setEditAccount] = useState(false);
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [accRes, txnRes] = await Promise.all([
        bankAccountService.getAccount(accountId),
        bankAccountService.getTransactions(accountId),
      ]);
      setAccount(accRes.data);
      setTxns(txnRes.data || []);
    } catch (err) {
      setError('Failed to load bank account');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [accountId]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(''), 2000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" /></div>;
  }
  if (!account) {
    return <div className="text-center py-20 text-gray-400 text-sm">Bank account not found.</div>;
  }

  return (
    <div className="space-y-6 pb-10">
      <style>{`
        .bank-card-sweep {
          position: absolute;
          inset: 0;
          background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%);
          background-size: 250% 100%;
          background-position: 100% 0;
          animation: bankSweep 3.5s ease-in-out infinite;
        }
        @keyframes bankSweep {
          0% { background-position: 160% 0; }
          100% { background-position: -60% 0; }
        }
      `}</style>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-control shadow-lg text-sm font-medium">
          <CheckCircle2 className="w-4 h-4" />
          {toast}
        </div>
      )}

      <button onClick={() => navigate('/bank-accounts')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" />
        Back to Bank Accounts
      </button>

      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{account.bank_name}</h1>
          <p className="text-sm text-gray-500 font-medium tracking-tight">{account.account_owner} — {account.branch_location}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditAccount(true)} className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all">
            <Pencil className="w-4 h-4" />
            Edit Account
          </button>
          <button onClick={() => setTxnModal('deposit')} className="flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-all">
            <Plus className="w-4 h-4" />
            Deposit
          </button>
          <button onClick={() => setTxnModal('withdraw')} className="flex items-center gap-1.5 bg-red-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-red-500/20 hover:bg-red-700 transition-all">
            <Minus className="w-4 h-4" />
            Withdraw
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          icon={Landmark}
          title="Account Number"
          mainValue={account.account_number}
          subLabel="Status"
          subValue={account.status === 'active' ? 'Active' : 'Inactive'}
          gradient="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-900"
          glow="bg-purple-400"
        />
        <SummaryCard
          icon={Wallet}
          title="Current Balance"
          mainValue={fmt(account.current_balance)}
          mainValueClassName={account.current_balance < 0 ? 'text-red-700' : ''}
          subLabel="Total Deposits"
          subValue={fmt(account.total_deposits)}
          gradient="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700"
          glow="bg-green-300"
        />
        <SummaryCard
          icon={ArrowDownCircle}
          title="Total Withdrawn"
          mainValue={fmt(account.total_withdrawals)}
          subLabel="This Month"
          subValue={fmt(account.this_month_withdraw)}
          gradient="bg-gradient-to-br from-rose-500 via-red-600 to-red-800"
          glow="bg-red-300"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <div>
        <h2 className="text-sm font-bold text-gray-800 mb-3">Transaction History</h2>
        <div className="grid grid-cols-1 gap-2">
          {txns.length === 0 ? (
            <div className="bg-white p-10 text-center rounded-card border border-dashed border-gray-200">
              <p className="text-gray-400 font-medium italic">No transactions yet.</p>
            </div>
          ) : (
           txns.map((txn) => {
              const isDeposit = txn.type === 'deposit';
              const txnLabel = (isDeposit ? 'Deposit #' : 'Withdraw #') + txn.transaction_id.replace('TXN-', '');
              const mainLine = isDeposit ? txn.person_name : (txn.purpose || txn.person_name || '—');
              return (
                <div key={txn.transaction_id} className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ' + (isDeposit ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')}>
                        {isDeposit ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownLeft className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{mainLine}</p>
                        <p className="text-xs text-gray-400">{txn.date} · {txnLabel}</p>
                      </div>
                    </div>
                    <p className={'text-sm font-bold ' + (isDeposit ? 'text-emerald-600' : 'text-red-600')}>
                      {isDeposit ? '+' : '-'}{fmt(txn.amount)}
                    </p>
                  </div>
      <div className="px-4 pb-4 pt-1 border-t border-gray-50 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Description</p>
                      <p className="text-sm text-gray-700">{txn.description || '—'}</p>
                      {!isDeposit && txn.person_name && <p className="text-xs text-gray-400 mt-1">Person: {txn.person_name}</p>}
                      {txn.reference_number && <p className="text-xs text-gray-400 mt-1">Transaction ID: {txn.reference_number}</p>}
                      {txn.created_by_name && <p className="text-xs text-gray-400 mt-1">Recorded by {txn.created_by_name}</p>}
                      {txn.edit_history && txn.edit_history.length > 0 && (
                        <button
                          onClick={() => setExpandedTxn(expandedTxn === txn.transaction_id ? null : txn.transaction_id)}
                          className="text-xs font-bold text-amber-600 mt-2 hover:underline"
                        >
                          {expandedTxn === txn.transaction_id ? 'Hide' : 'View'} edit history ({txn.edit_history.length})
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => setEditTxn(txn)}
                        className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-dark"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteTxn(txn)}
                        className="flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {expandedTxn === txn.transaction_id && txn.edit_history && txn.edit_history.length > 0 && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-50 bg-gray-50/50 space-y-2">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Edit History</p>
                      {txn.edit_history.slice().reverse().map((h, idx) => (
                        <div key={idx} className="text-xs text-gray-600 bg-white rounded-lg border border-gray-100 p-2.5">
                          <p className="font-bold text-gray-700">
                            Edited by {h.edited_by_name || 'Unknown'} — {h.edited_at ? new Date(h.edited_at).toLocaleString() : ''}
                          </p>
                          <p className="mt-1 text-gray-500">
                            Previous: {fmt(h.previous.amount)} · {h.previous.purpose || h.previous.person_name} · {h.previous.date}
                            {h.previous.description ? ' · ' + h.previous.description : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {txnModal && (
        <TxnModal
          type={txnModal}
          accountId={accountId}
          currentBalance={account.current_balance}
          onClose={() => setTxnModal(null)}
          onDone={() => { setTxnModal(null); setToast((txnModal === 'deposit' ? 'Deposit' : 'Withdrawal') + ' recorded successfully!'); loadData(); }}
        />
      )}

      {editTxn && (
        <EditTxnModal
          txn={editTxn}
          accountId={accountId}
          onClose={() => setEditTxn(null)}
          onDone={() => { setEditTxn(null); setToast('Transaction updated successfully!'); loadData(); }}
        />
      )}

      {editAccount && (
        <EditAccountModal
          account={account}
          onClose={() => setEditAccount(false)}
          onDone={() => { setEditAccount(false); setToast('Bank account updated successfully!'); loadData(); }}
        />
      )}

      {deleteTxn && (
        <DeleteTxnModal
          txn={deleteTxn}
          accountId={accountId}
          onClose={() => setDeleteTxn(null)}
          onDone={() => { setDeleteTxn(null); setToast('Transaction deleted successfully!'); loadData(); }}
        />
      )}
    </div>
  );
};
export default BankAccountDetail;